// Libraries
import clsx from "clsx";
import { useState } from "react";
import { Box, CircularProgress, Fade, Grid, IconButton, SvgIcon } from "@mui/material";
import {
	GRID_CHECKBOX_SELECTION_COL_DEF,
	GridColDef,
	GridColumnHeaderParams,
	GridRowId,
	GridRowParams,
	GridValidRowModel,
	useGridApiRef,
} from "@mui/x-data-grid-premium";

// Components
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import TypographyCell from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";

// Icons
import { ReactComponent as TransferInIcon } from "@/assets/icons/dataPages/inventory/transfer-in.svg";
import { ReactComponent as TransferOutIcon } from "@/assets/icons/dataPages/inventory/transfer-out.svg";
import { ReactComponent as TransferSwapIcon } from "@/assets/icons/dataPages/inventory/transfer-swap.svg";
import { ReactComponent as NotAllowedIcon } from "@/assets/icons/dataPages/inventory/not-allowed.svg";

// Constants
import { CELL_CLASS_NAME, CUSTOM_PROPS, SLOTS } from "../commonProps";

// Types
import { TTransferType } from "../../types";
import { IInventoryTradeVehicles, TSearchTransferRequest, TTradingWith, TTransferInOut } from "../../types/dealerTrades";
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";
import { LoadingSpinner } from "@/Layouts/components/LoadingSpinner";
import TransferAmountCell from "./TransferAmountCell";
import TransferAmountDialog from "./TransferAmountDialog";
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import actions from "@/Constants/actionsList";
import { defaultCellRenderer } from "@/Layouts/components/Tables/DataSmartTable/helpers";

// Helpers
import { or } from "@/Common/helpers/sonar";
import { SelectionAndFilterCheckbox } from "../../SelectionAndFilterCheckbox";
import { InventoryTab, TRANSFER_IN } from "../../constants";
import { useInventoryContext } from "../../contexts/InventoryFilterContext";

// Local Helpers
function renderHeaderHandler(params: GridColumnHeaderParams) {
	return <TypographyHeader fontSize="14px" fontLineHeight="14px" params={params} />;
}

type TTransferRequestTableProps = {
	table: string;
	locationId: string;
	commissionCycleId?: string;
	payPeriodId: string;
	iconType: TTransferType;
	transferType: TTransferInOut;
	inventoryTradeVehicles: IInventoryTradeVehicles[];
	disableTransferAmountDialog?: boolean;
	enableActions?: boolean;
	locationsOptions: {
		isLoadingLocations: boolean;
		mappedLocations: { value: string; label: string }[];
	};
	onCreateTransferRequest: (data?: IInventoryTradeVehicles) => void;
	isLoadingInventory: boolean;
	hasNextPage: boolean;
	isCreatingTransferRequest?: boolean;
	defaultSearchTransferRequest?: Partial<TSearchTransferRequest>;
	isCurrentPayPeriod: boolean;
};

type TSelectStoreOutProps = {
	optionsCustomComponents: {
		icon: JSX.Element;
		onClick: (value: number) => void;
		isCreatingTransferRequest?: boolean;
		defaultValue?: number;
		disabled?: boolean;
	};
};

const SelectStoreOut = (props: Readonly<TSelectStoreOutProps>) => {
	const { optionsCustomComponents } = props;
	const { icon, onClick, isCreatingTransferRequest, defaultValue, disabled = false } = optionsCustomComponents;
	const [isOpen, setIsOpen] = useState(false);

	const onCloseDialog = () => {
		setIsOpen(false);
	};

	const onConfirm = (internalValue: number) => {
		onClick(internalValue);
	};

	return (
		<Box className="flex">
			<IconButton
				onClick={() => setIsOpen(true)}
				className={clsx({
					"!ml-4 !px-[10px] !py-[11px] !mt-[-5px]": true,
					"cursor-not-allowed opacity-50 bg-disabled": disabled,
				})}
				disabled={disabled}
			>
				{isCreatingTransferRequest ? <CircularProgress size={20} color="inherit" /> : icon}
			</IconButton>
			<TransferAmountDialog isOpen={isOpen} onCloseDialog={onCloseDialog} defaultValue={defaultValue ?? 0} onConfirm={onConfirm} />
		</Box>
	);
};

export const TransferRequestTable = (props: Readonly<TTransferRequestTableProps>) => {
	const {
		iconType,
		transferType,
		disableTransferAmountDialog,
		inventoryTradeVehicles,
		locationsOptions,
		onCreateTransferRequest,
		isLoadingInventory,
		hasNextPage,
		isCreatingTransferRequest,
		defaultSearchTransferRequest,
		locationId,
		commissionCycleId,
		payPeriodId,
		table,
		enableActions = true,
		isCurrentPayPeriod
	} = props;

	const { clearFilterSelection } = useInventoryContext();

	const { mappedLocations, isLoadingLocations } = locationsOptions;

	const isLoading = isLoadingInventory || isLoadingLocations;

	const apiRef = useGridApiRef();

	const [rowSelected, setRowSelected] = useState<GridRowId>("");

	// Permissions
	const { useAuth } = useAuthorization();
	const viewInvoiceCost = useAuth(actions.VIEW_DEALER_TRADES_INVOICE_COST);

	const columns: GridColDef[] = [
		{
			field: GRID_CHECKBOX_SELECTION_COL_DEF.field,
			width: 50,
			filterable: false,
			sortable: false,
			resizable: false,
			renderHeader: () => <></>,
			renderCell: (params) => {
				return (
					<SelectionAndFilterCheckbox
						onChangeCallback={(checked: boolean, id: string) => setRowSelected(checked ? id : "")}
						_id={params.row._id as string}
						origin={InventoryTab.DEALER_TRADES}
						table={table}
						row={params.row as IInventoryTradeVehicles}
						pathToVinNumber="vin"
						className="!ml-[-10px]"
						isSingleSelect
						disableFilter={transferType === TRANSFER_IN || table === "editTransferRequest"}
						disabled={params?.row?.tradingWith?.some?.(
							(inventory: TTradingWith) => inventory.isTradeActive || inventory.isBlocked
						)}
						commissionCycleId=""
						payPeriodId=""
					/>
				);
			},
		},
		{
			field: "_id",
			headerName: "ID",
			minWidth: 10,
			width: 10,
			filterable: false,
			resizable: false,
		},
		{
			field: "not-allowed-icon",
			width: 35,
			headerName: "",
			valueGetter: (_, rowItem) => {
				const { tradingWith } = rowItem;
				const firstItem = tradingWith[0];

				const inUseWith = mappedLocations?.find((item) => item.value === firstItem?._id)?.label;

				return {
					...firstItem,
					inUseWith,
				};
			},
			renderCell: (param) => {
				// TTradingWith
				const { value } = param;

				if (!value.isBlocked && !value.isTradeActive) {
					return null;
				}

				let title = "";
				if (value.inUseWith) {
					title = `Currently in trade with ${param.value.inUseWith}`;
				}
				if (value.isBlocked) {
					title = "GPS device transfer is in progress";
				}

				return (
					<TypographyCell
						hasTooltip
						className="!flex justify-center items-center cursor-pointer !mt-1"
						forceTooltip
						tooltipProps={{ title }}
					>
						<NotAllowedIcon />
					</TypographyCell>
				);
			},
		},
		{
			field: "locationId",
			flex: 1,
			headerName: "Origin Store",
			renderHeader: renderHeaderHandler,
			cellClassName: () => CELL_CLASS_NAME,
			valueGetter: (rowLocationId: string) => mappedLocations?.find((item) => item.value === rowLocationId)?.label ?? "",
		},
		{
			field: "type",
			headerName: "Type",
			width: 70,
			renderHeader: renderHeaderHandler,
			cellClassName: () => CELL_CLASS_NAME,
		},
		{
			field: "status",
			headerName: "Status",
			width: 90,
			renderHeader: renderHeaderHandler,
			cellClassName: () => CELL_CLASS_NAME,
		},
		{
			field: "stock",
			headerName: "Stock #",
			width: 90,
			renderHeader: renderHeaderHandler,
			cellClassName: () => CELL_CLASS_NAME,
		},
		{
			field: "vin",
			width: 120,
			headerName: "VIN",
			renderHeader: renderHeaderHandler,
			cellClassName: () => CELL_CLASS_NAME,
		},
		{
			field: "make",
			width: 120,
			headerName: "Make",
			renderHeader: renderHeaderHandler,
			cellClassName: () => CELL_CLASS_NAME,
		},
		{
			field: "model",
			width: 120,
			headerName: "Model",
			renderHeader: renderHeaderHandler,
			cellClassName: () => CELL_CLASS_NAME,
		},
		{
			field: "trim",
			width: 120,
			headerName: "Trim",
			renderHeader: renderHeaderHandler,
			cellClassName: () => CELL_CLASS_NAME,
		},
		{
			field: "colorExterior",
			width: 120,
			headerName: "Ext. Color",
			renderHeader: renderHeaderHandler,
			cellClassName: () => CELL_CLASS_NAME,
		},
		{
			field: "year",
			width: 80,
			headerName: "Year",
			renderHeader: renderHeaderHandler,
			cellClassName: () => CELL_CLASS_NAME,
		},
		{
			field: "invoiceCost",
			minWidth: 110,
			headerName: "Invoice/Cost",
			renderHeader: renderHeaderHandler,
			type: "number",
			align: "right",
			cellClassName: () => CELL_CLASS_NAME,
			renderCell: defaultCellRenderer,
		},
		{
			field: "transferAmount",
			width: 85,
			headerName: "Transfer Amount",
			renderHeader: renderHeaderHandler,
			type: "number",
			align: "right",
			cellClassName: () => CELL_CLASS_NAME,
			valueGetter: (_, internalRow) => internalRow?.transferAmount,
			renderCell: (params) => {
				return (
					<TransferAmountCell
						table={table}
						params={params}
						locationId={locationId}
						commissionCycleId={commissionCycleId}
						payPeriodId={payPeriodId}
						section="transferRequest"
						disableOpenDialog={disableTransferAmountDialog}
					/>
				);
			},
		},
	];

	const row = inventoryTradeVehicles.find((item) => item._id === rowSelected);

	const requestActionHandler = (transferAmount: number) => {
		if (isCurrentPayPeriod) {
			const newRow = { ...row, transferAmount } as IInventoryTradeVehicles;
			onCreateTransferRequest(newRow);
			clearFilterSelection([{ table }]);
		}
	};

	function getTransferIcon() {
		const iconMap: Record<TTransferType, JSX.Element> = {
			"Transfer In": <TransferInIcon />,
			"Transfer Out": <TransferOutIcon />,
			"Asset Swap": <TransferSwapIcon />,
			"": <></>,
		};
		return iconMap[iconType];
	}

	function isRowPendingTrade(tradingWith: string[]) {
		return !!tradingWith.length;
	}

	function isRowSelectable(params: GridRowParams<GridValidRowModel>) {
		const { row: rowItem } = params;
		const { tradingWith = [] } = rowItem;

		const isSameVinNumber = rowItem.vin === defaultSearchTransferRequest?.vinNumber;
		const isBeingTraded = isRowPendingTrade(tradingWith);

		return !or(isSameVinNumber, isBeingTraded);
	}

	if (isLoading) {
		return (
			<Grid className="mt-[20px]">
				<TableLoader toolbar buttons={2} rows={5} content multiTitles={8} toolbarSx={{ marginBottom: "10px" }} />
			</Grid>
		);
	}

	const usedColumns = viewInvoiceCost ? columns : columns.filter((column) => column.field !== "invoiceCost");

	return (
		<DataSmartTable
			apiRef={apiRef}
			columns={usedColumns}
			rows={inventoryTradeVehicles}
			maxRows={10}
			enableCustomScrollbars
			disableColumnMenu
			checkboxSelection={enableActions}
			slots={enableActions ? SLOTS : {}}
			isRowSelectable={isRowSelectable}
			slotProps={
				enableActions
					? {
							toolbar: {
								titleExpandible: {
									className: "mt-[5px] [&_.toolbar-actions]:!right-0 [&_.toolbar-actions]:!bottom-0",
									hide: true,
								},
								customComponent: (
									<>
										{hasNextPage && (
											<Fade in={hasNextPage} timeout={400}>
												<Box className="my-[16px]">
													<LoadingSpinner label="Inventory data is still loading..." className="items-center" />
												</Box>
											</Fade>
										)}
									</>
								),
								customAction: {
									hide: !rowSelected,
									CustomComponent: SelectStoreOut,
									optionsCustomComponents: {
										icon: <SvgIcon className="mt-[5px]" fontSize="medium" component={getTransferIcon}/>,
										onClick: requestActionHandler,
										defaultValue: row?.transferAmount,
										isCreatingTransferRequest,
										disabled: !isCurrentPayPeriod,
									},
								},
								filter: {
									data: inventoryTradeVehicles || [],
									hide: !!rowSelected,
									disabled: false,
								},
								search: {
									useQuickFilter: true,
									data: inventoryTradeVehicles,
									hide: !!rowSelected,
								},
								expand: {
									hide: true,
								},
							},
							noRowsOverlay: {
								title: "No inventory results found",
								className: "!text-montserrat",
							},
					  }
					: {}
			}
			hideFooter
			customProps={{
				...CUSTOM_PROPS,
				tableClassName: {
					...CUSTOM_PROPS.tableClassName,
					"mt-[12px]": true,
				},
			}}
			rowHeight={25}
			autoHeight={false}
			columnVisibilityModel={{ _id: false }}
			onRowSelectionModelChange={([rowId = ""]) => setRowSelected(rowId)}
			disableMultipleRowSelection
			disableRowSelectionOnClick
		/>
	);
};
